import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HotelCard from '../components/HotelCard';
import { Heart, ArrowLeft, Search } from 'lucide-react';
import { useFavorites } from '../context/FavoritesContext';
import DefaultSEO from '../components/DefaultSEO';

const SavedHotelsPage = () => {
    const navigate = useNavigate();
    const [savedHotels, setSavedHotels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { toggleFavorite } = useFavorites();

    useEffect(() => {
        const fetchSaved = async () => {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                navigate('/login');
                return;
            }

            try {
                const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
                const res = await fetch(`${apiBase}/auth/favorites`, {
                    headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
                });

                if (res.ok) {
                    const data = await res.json();
                    if (data.success && data.data) {
                        setSavedHotels(data.data);
                    }
                } else if (res.status === 401) {
                    navigate('/login');
                }
            } catch (error) {
                console.error('Failed to fetch saved hotels', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchSaved();
    }, [navigate]);

    const handleUnfavorite = (hotelId) => {
        // HotelCard already calls toggleFavorite, so we only need to update the local state to remove the card
        setSavedHotels(prev => prev.filter(item => item.id !== hotelId));
    };

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-4 md:py-8 mt-5 md:mt-16">
            <DefaultSEO title="Saved Hotels | ZoyoTrip" />
            <div className="max-w-5xl mx-auto pb-20 text-start">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-0">Saved Hotels</h2>
                </div>

                {isLoading ? (
                    <div className="text-center py-12">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : savedHotels.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center text-red-500 mx-auto mb-4 shadow-sm">
                            <Heart className="w-8 h-8 fill-current" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 mb-1">No saved hotels yet</h3>
                        <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                            Tap the heart icon on any hotel card to add it to your wishlist and view it here later.
                        </p>
                        <button 
                            onClick={() => navigate('/search')}
                            className="inline-flex items-center px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-sm border-0"
                        >
                            <Search className="w-4 h-4 mr-2" /> Explore Hotels
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {savedHotels.map((hotel) => (
                            <HotelCard 
                                key={hotel.id} 
                                {...hotel} 
                                onViewDetails={() => navigate(`/hotel/${hotel.id}`)}
                                onBookNow={() => {
                                    const price = hotel.price || 1500;
                                    navigate(`/checkout/${hotel.id}`, {
                                        state: {
                                            hotel: {
                                                id: hotel.id,
                                                name: hotel.title,
                                                location: hotel.location,
                                                image: hotel.images?.[0] || "",
                                                amenities: hotel.amenities || []
                                            },
                                            room: { type: "Standard Room", capacity: 2, pricePerNight: price },
                                            dates: { startDate: new Date(), endDate: new Date(new Date().setDate(new Date().getDate() + 1)) },
                                            guests: 2,
                                            pricing: { 
                                                totalNights: 1, 
                                                totalPrice: price, 
                                                taxes: price * 0.18, 
                                                finalAmount: price + (price * 0.18) 
                                            }
                                        }
                                    });
                                }}
                                onFavorite={() => handleUnfavorite(hotel.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SavedHotelsPage;
